import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { Table, Whisper, Pagination } from "rsuite";
import { useHistory } from "react-router-dom";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { TextCell, MenuPopover } from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { ActionsInsuranceTable } from "../../core/redux/slices/sliceInsuranceTable";
import {
  ApiSchemaInsuranceType,
  ApiSchemaInsuranceTypePaginated,
} from "../../models/api/insuranceType";
import { ApiInsuranceTypes } from "../../core/apis/insuranceTypes";
import { sortTableData } from "../../utils/tableFilter";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import { InsuranceStatusToggle } from "../../components/insuranceStatusToggle.component";
import editIcon from "../../assets/img/edit-icon.svg";
import { showToast } from "../../components/toast.component";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../hooks";
import { TableHeader } from "../../components";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaInsuranceType & {
  sl?: number;
  checked?: boolean;
};

export const InsuranceList = () => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const {
    pageNumber,
    pageSize,
    isLoading,
    searchKey,
    nextPageToken,
    shouldRefetch,
  } = useAppSelector((state) => state.insuranceTable);
  const customParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      NextPageToken: nextPageToken,
      SearchKey: searchKey,
    };
  }, [pageNumber, pageSize, nextPageToken, searchKey]);
  const { apiData, refetch } = useTablePaginationSearchFilter<
    ApiSchemaInsuranceTypePaginated,
    typeof ActionsInsuranceTable
  >(ActionsInsuranceTable, {
    customParams,
    apiRoute: AppRouteApi.InsuranceTypes.Paginated(),
  });
  const dispatch = useAppDispatch();
  const [sortBy, setSortBy] = useState<keyof TableData>("sl");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterStatus, toggleFilterStatus] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );

  useEffect(
    () =>
      setTableData(
        apiData?.list?.map((insuranceType, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          ...insuranceType,
        })) ?? []
      ),
    [apiData, pageNumber, pageSize]
  );

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const updateDeleted = (sKeys: string[]) => async (res?: TableData[]) =>
    res?.filter((i) => !sKeys.includes(i.sKey)) || [];

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected insurance?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsInsuranceTable.setIsLoading(true));
          try {
            const selectedIds = selected
              .filter((s) => s.sKey)
              .map((s) => s.sKey);
            const deleted = await deleteItem(selectedIds);
            ApiInsuranceTypes.GetInsuranceTypes.Mutate(updateDeleted(deleted));
          } catch (e) {
            showToast("Error deleting insurances", "error");
          } finally {
            setIsDeleting(false);
            dispatch(ActionsInsuranceTable.setIsLoading(false));
            dispatch(ActionsInsuranceTable.setRefetch(true));
          }
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const deleteItem = async (sKey: string[]) =>
    await ApiInsuranceTypes.Delete(sKey);

  const handleSelectMenu = (
    eventKey: string,
    event: Event,
    sKey?: string,
    rowData?: any,
    isDeActivated?: boolean
  ) => {
    if (eventKey === "delete" && sKey) {
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this insurance?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsInsuranceTable.setIsLoading(true));
          try {
            const deleted = await deleteItem([sKey]);
            ApiInsuranceTypes.GetInsuranceTypes.Mutate(updateDeleted(deleted));
          } catch (e: any) {
            const message =
              JSON.parse(`${e.message}`)?.data?.message ??
              "Error deleting insurance";
            showToast(message, "error");
          } finally {
            dispatch(ActionsInsuranceTable.setIsLoading(false));
          }
        },
      });
    } else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.Insurance.UpdateById(sKey));
  };

  const filteredTableData = useMemo(() => {
    const statusFiltered =
      filterStatus === 2
        ? tableData
        : tableData.filter((td) => +td.isDisabled === filterStatus);
    return sortTableData(statusFiltered, sortBy, sortAsc);
  }, [filterStatus, sortAsc, sortBy, tableData]);

  const handleStatusChange = (sKey: string, isDisabled: boolean) => {
    setTableData((prev) =>
      prev.map((item) => (item.sKey === sKey ? { ...item, isDisabled } : item))
    );
  };

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Insurance Company"
        redirectPath={AppRouteUi.Insurance.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsInsuranceTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />

      <Table
        autoHeight
        data={filteredTableData}
        loading={isLoading}
        onScroll={() => triggerRef.current?.close()}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Insurance Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column align="center" width={35}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>
        <Column align="center" width={60} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="S/L">S/L</span>
          </HeaderCell>
          <TextCell component="Insurance" dataKey="sl" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Name">Name</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => rowData.displayName ?? rowData.name}
          </Cell>
        </Column>

        <Column align="center" flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Code">Subtitle</span>
          </HeaderCell>
          <TextCell component="Insurance" dataKey="subtitle" />
        </Column>

        <Column align="center" flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Code">Payer ID</span>
          </HeaderCell>
          <TextCell component="Insurance" dataKey="payerID" />
        </Column>

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span
              title="Enabled"
              className="click"
              onClick={() => toggleFilterStatus()}
            >
              Enabled
              <BooleanToggleFilterIcon
                filterValue={
                  filterStatus === 0
                    ? "true"
                    : filterStatus === 1
                    ? "false"
                    : "default"
                }
                className="ms-2 toggle-filter-icon"
              />
            </span>
          </HeaderCell>
          <Table.Cell>
            {(insurance: ApiSchemaInsuranceType) => (
              <InsuranceStatusToggle
                insurance={insurance}
                handleStatusChange={handleStatusChange}
              />
            )}
          </Table.Cell>
        </Column>

        <Column align="center" fixed="right">
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <Whisper
                placement="left"
                trigger="click"
                ref={triggerRef}
                speaker={(props, ref) => {
                  const { className, left, top, onClose } = props;
                  return (
                    <MenuPopover
                      ref={ref}
                      className={className}
                      style={{ left, top }}
                      onClose={onClose}
                      hideDelete={true}
                      onSelect={(eventKey: string, event: Event) =>
                        handleSelectMenu(eventKey, event, rowData.sKey)
                      }
                    />
                  );
                }}
              >
                <img
                  src={editIcon}
                  alt="Update/Delete"
                  className="me-1 click"
                  height={15}
                />
              </Whisper>
            )}
          </Cell>
        </Column>
      </Table>
      {apiData?.list && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={apiData?.list.length}
            pages={Math.ceil(apiData?.totalCount / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) => dispatch(ActionsInsuranceTable.goToPage(page))}
          />
        </div>
      )}
    </div>
  );
};
